// tabs/cleanliness/cleanliness.adapter.ts
import type { AnalysisResult, GhostNoteData } from "../../contracts/scoring";
import type { CleanlinessModuleResult } from "./cleanliness.contract";

function safeArray<T>(v: any): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function safeNumber(v: any, fallback = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

/**
 * ✅ Cleanliness is VALID even if ghostNotes.length === 0.
 * "Non calculable" only if signal is not measurable OR debug contract missing.
 */
export function adaptToCleanlinessModule(result: AnalysisResult): CleanlinessModuleResult {
  const dbg: any = result?.session?.metrics?.debug || {};
  const isMeasurable = !!dbg.isMeasurable;
  const nonSilenceRatio = safeNumber(dbg.non_silence_ratio, 0);
  const rmsDb = safeNumber(dbg.rms_db, -240);

  const duration = safeNumber(result?.session?.duration, 0);
  const durationMin = Math.max(0.1, duration / 60);

  // ghostNotes vient du DSP normalize (dspAdapter) → result.tone.ghostNotes
  const ghostNotes = safeArray<GhostNoteData>(result?.tone?.ghostNotes);
  const ghostCount = ghostNotes.length; // ✅ 0 est une info valide
  const ghostsPerMinute = ghostCount / durationMin;

  // seuil simple pour commencer (tu le brancheras ensuite sur severityEngine)
  const thresholdErrPerMin = 25;

  // VRAI "insufficient data" : pas mesurable OU debug absent / ratio nul OU durée invalide
  if (!isMeasurable || nonSilenceRatio <= 0 || duration <= 0) {
    return {
      status: "INSUFFICIENT_DATA",
      score: 0,
      isSimulated: false,
      message: "Analyse de propreté non calculable sur ce signal.",
      data: {
        ghostNotes,
        duration,
        ghostsPerMinute: safeNumber(ghostsPerMinute, 0),
        thresholdErrPerMin,
        details: { nonSilenceRatio, isMeasurable, rmsDb }
      }
    };
  }

  // Score : 0 err/min => 100
  // threshold => ~70
  // 2x threshold => ~40
  const ratio = thresholdErrPerMin > 0 ? ghostsPerMinute / thresholdErrPerMin : 0;
  const rawScore = 100 - ratio * 30;
  const score = Math.round(clamp(rawScore, 0, 100));

  const message =
    ghostCount === 0
      ? "Propreté parfaite : aucune ghost note détectée."
      : "Ghost notes détectées : votre propreté peut être améliorée.";

  return {
    status: "OK",
    score,
    isSimulated: false,
    message,
    data: {
      ghostNotes,
      duration,
      ghostsPerMinute,
      thresholdErrPerMin,
      details: { nonSilenceRatio, isMeasurable, rmsDb }
    }
  };
}
